import { fetchReviews } from '@theme/review-api';

const MOBILE_BREAKPOINT = 750;

/**
 * Custom element that displays reviews in a horizontal carousel.
 * Desktop: 3 per view. Mobile: 1 per view. Auto-cycles with arrows.
 */
class ReviewCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Array} */
  #reviews = [];
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number} */
  #slidesPerView = 1;
  /** @type {number} */
  #totalSlides = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #intervalId;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    const productId = this.dataset.productId;
    if (productId) {
      this.loadReviews(productId);
    }
    this.addEventListener('mouseenter', this.#pauseAutoplay);
    this.addEventListener('mouseleave', this.#resumeAutoplay);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#clearInterval();
    this.#resizeObserver?.disconnect();
    this.removeEventListener('mouseenter', this.#pauseAutoplay);
    this.removeEventListener('mouseleave', this.#resumeAutoplay);
  }

  #pauseAutoplay = () => {
    this.#clearInterval();
  };

  #resumeAutoplay = () => {
    if (this.#getMaxIndex() > 0) {
      this.#startAutoplay();
    }
  };

  #clearInterval() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }

  #startAutoplay() {
    this.#clearInterval();
    const maxIndex = this.#getMaxIndex();
    const seconds = parseInt(this.dataset.autoplayInterval || '5', 10);
    if (seconds > 0 && maxIndex > 0) {
      this.#intervalId = setInterval(() => {
        if (document.hidden) return;
        const next = this.#currentIndex >= maxIndex ? 0 : this.#currentIndex + 1;
        this.#currentIndex = next;
        this.#updatePosition();
        this.#updateArrows();
        this.#clearInterval();
        this.#startAutoplay();
      }, seconds * 1000);
    }
  }

  async loadReviews(productId) {
    if (!productId || productId === 'PREVIEW-ID') return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const loadingIndicator = this.querySelector('[ref="loadingIndicator"]');
    const carouselContent = this.querySelector('[ref="carouselContent"]');
    const track = this.querySelector('[ref="track"]');

    if (loadingIndicator) loadingIndicator.classList.remove('visually-hidden');
    if (carouselContent) carouselContent.classList.add('visually-hidden');

    try {
      const limit = parseInt(this.dataset.reviewsLimit || '9', 10);
      const response = await fetchReviews({ productId, page: 1, limit });

      if (this.#abortController.signal.aborted) return;

      if (loadingIndicator) loadingIndicator.classList.add('visually-hidden');

      if (!response || !response.reviews || response.reviews.length === 0) {
        this.classList.add('visually-hidden');
        return;
      }

      this.classList.remove('visually-hidden');
      this.#reviews = response.reviews;
      this.#setupCarousel();
      this.classList.toggle('review-carousel--single', this.#reviews.length === 1);
      this.#attachEventListeners();
      this.#setupResizeObserver();
      this.#resumeAutoplay();

      if (carouselContent) carouselContent.classList.remove('visually-hidden');
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error loading review carousel:', error);
      if (loadingIndicator) loadingIndicator.classList.add('visually-hidden');
    }
  }

  #setupCarousel() {
    this.#updateSlidesPerView();
    this.#buildSlides();
    this.#currentIndex = 0;
    this.#updatePosition();
    this.#updateArrows();
  }

  #updateSlidesPerView() {
    this.#slidesPerView = window.innerWidth >= MOBILE_BREAKPOINT ? 3 : 1;
    this.#totalSlides = this.#reviews.length;
  }

  #buildSlides() {
    const track = this.querySelector('[ref="track"]');
    if (!track) return;

    const html = this.#reviews
      .map(
        (r, i) =>
          `<div class="review-carousel-slide" data-slide-index="${i}"><div class="review-carousel-item-wrap">${this.#renderReviewItem(r)}</div></div>`
      )
      .join('');
    track.innerHTML = html;
  }

  #renderReviewItem(review) {
    const date = new Date(review.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    let starsHtml = '';
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= review.rating;
      starsHtml += `
        <svg class="review-carousel-item-star ${!isFilled ? 'empty' : ''}" viewBox="0 0 32 32" role="presentation">
          <path d="M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z" />
        </svg>
      `;
    }
    const text = review.review_text ? `<div class="review-carousel-item-text">${this.#escapeHtml(review.review_text)}</div>` : '';
    return `
      <div class="review-carousel-item">
        <div class="review-carousel-item-rating" aria-label="${review.rating} out of 5">
          <div class="review-carousel-item-stars">${starsHtml}</div>
        </div>
        ${text}
        <div class="review-carousel-item-meta">
          <span class="review-carousel-item-name">${review.customer_name || 'Anonymous'}</span>
          <span class="review-carousel-item-date">${date}</span>
          </div>
          ${review.verified_purchase ? '<span class="review-carousel-item-verified">✓ Verified</span>' : ''}
      </div>
    `;
  }

  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  #attachEventListeners() {
    const prevBtn = this.querySelector('[ref="prevButton"]');
    const nextBtn = this.querySelector('[ref="nextButton"]');
    prevBtn?.addEventListener('click', () => this.#goTo(this.#currentIndex - 1));
    nextBtn?.addEventListener('click', () => this.#goTo(this.#currentIndex + 1));
  }

  #setupResizeObserver() {
    const viewport = this.querySelector('[ref="viewport"]');
    if (!viewport) return;

    this.#resizeObserver = new ResizeObserver(() => {
      const oldPerView = this.#slidesPerView;
      this.#updateSlidesPerView();
      if (oldPerView !== this.#slidesPerView) {
        this.#buildSlides();
      }
      this.#currentIndex = Math.min(this.#currentIndex, this.#getMaxIndex());
      this.#updatePosition();
      this.#updateArrows();
    });
    this.#resizeObserver.observe(viewport);
  }

  #getMaxIndex() {
    return Math.max(0, this.#reviews.length - this.#slidesPerView);
  }

  #goTo(index) {
    const maxIndex = this.#getMaxIndex();
    if (maxIndex <= 0) return;
    this.#currentIndex = Math.max(0, Math.min(index, maxIndex));
    this.#updatePosition();
    this.#updateArrows();
    this.#clearInterval();
    this.#startAutoplay();
  }

  #updatePosition() {
    const track = this.querySelector('[ref="track"]');
    const viewport = this.querySelector('[ref="viewport"]');
    if (!track || !viewport) return;

    const gap = 16;
    const slideWidthPx = (viewport.offsetWidth - (this.#slidesPerView - 1) * gap) / this.#slidesPerView;
    track.style.setProperty('--slide-width', `${slideWidthPx}px`);
    track.style.transform = `translateX(-${this.#currentIndex * (slideWidthPx + gap)}px)`;
  }

  #updateArrows() {
    const maxIndex = this.#getMaxIndex();
    const prevBtn = this.querySelector('[ref="prevButton"]');
    const nextBtn = this.querySelector('[ref="nextButton"]');
    if (prevBtn) {
      prevBtn.style.visibility = maxIndex > 0 ? 'visible' : 'hidden';
      prevBtn.disabled = maxIndex <= 0 || this.#currentIndex <= 0;
    }
    if (nextBtn) {
      nextBtn.style.visibility = maxIndex > 0 ? 'visible' : 'hidden';
      nextBtn.disabled = maxIndex <= 0 || this.#currentIndex >= maxIndex;
    }
  }
}

if (!customElements.get('review-carousel')) {
  customElements.define('review-carousel', ReviewCarousel);
}
